import type { SurveyPage } from '../types/survey';
import type { ApiQuestion, ApiResponse } from '../internalTypes';
import { mapQuestion } from './mappers/question';
import { mapPageCtaConfig } from './mappers/survey/mapCtaButton';

/**
 * Maps the raw DTO survey pages and their questions into UI-friendly structure.
 *
 * @param api The raw API response envelope containing survey DTO pages.
 * @param surveyLanguage The resolved target language.
 * @param placeholders Optional dictionary mapping placeholder keys to replacement values.
 * @returns Array of mapped SurveyPage objects.
 */
export function mapSurveyPages(
  api: ApiResponse,
  surveyLanguage: string,
  placeholders?: Record<string, string>
): SurveyPage[] {
  const dto = api.cfmSurveyDTO;
  if (!dto || !Array.isArray(dto.surveyPages)) {
    return [];
  }

  return dto.surveyPages.map((page, index) => {
    const questions = (page.questionHolders ?? [])
      .map(holder => holder.question)
      .filter((q): q is ApiQuestion => Boolean(q))
      .map(q => mapQuestion(q, surveyLanguage, placeholders));

    const cta = mapPageCtaConfig(page.ctaConfig, surveyLanguage);

    return {
      id: page.id ?? `page_${index}`,
      title: page.title ?? `Page ${index + 1}`,
      questions,
      ...(cta ? { cta } : {}),
    };
  });
}
